/**
 * This browser's identities: the current user, and any it had before linking
 * to an existing account (unified-token-system, decision 8, answer 1).
 *
 * Linking a device gives the browser the account's user id and new keys. The
 * identity it had until then — possibly an unregistered one that minted
 * grants (acequia.grants) — would be gone, and with it the key every one of
 * those grants is rooted in: they could no longer be verified, renewed or
 * revoked here. So acceptDeviceLink keeps it: its user id, its key pair, and
 * the account it was linked into. A grant rooted in a kept identity is still
 * this browser's own — verified with its key, renewed and revoked with it.
 *
 * Kept identities never mint new grants; the current user does.
 */

import { acequiaReady, acequiaStore, getUserId, getUserKeys } from '../acequia/acequia.js'
import { jose } from '../deps.js'

const STORE_KEY = 'previousIdentities'
let cache = null

async function kidOf(keys) {
    return jose.calculateJwkThumbprint(await jose.exportJWK(keys.publicKey))
}

/**
 * The identities this browser had before, newest first.
 * @returns {Promise<Array<{ userId: string, kid: string, userKeys: CryptoKeyPair, linkedTo: string|null, keptAt: string }>>}
 */
export async function previousIdentities() {
    if (cache) return cache
    try {
        cache = (acequiaStore && await acequiaStore.getItem(STORE_KEY)) || []
    } catch {
        cache = []
    }
    return cache
}

/** Read the kept identities again on next use (another tab may have changed them). */
export function forgetCachedIdentities() {
    cache = null
}

/**
 * Keep an identity this browser is about to replace (acceptDeviceLink).
 * @param {object} identity
 * @param {string} identity.userId
 * @param {CryptoKeyPair} identity.userKeys
 * @param {string} [identity.linkedTo] - the account it was linked into
 */
export async function keepPreviousIdentity({ userId, userKeys, linkedTo = null }) {
    if (!userId || !userKeys?.publicKey || !acequiaStore) return null
    const list = [...await previousIdentities()]
    const kid = await kidOf(userKeys)
    if (list.some((p) => p.userId === userId && p.kid === kid)) return null
    const kept = { userId, kid, userKeys, linkedTo, keptAt: new Date().toISOString() }
    list.unshift(kept)
    await acequiaStore.setItem(STORE_KEY, list)
    cache = list
    return kept
}

/** Update a kept identity (e.g. once its alias is attested on a server). */
export async function updatePreviousIdentity(userId, updates) {
    const list = [...await previousIdentities()]
    const i = list.findIndex((p) => p.userId === userId)
    if (i < 0 || !acequiaStore) return null
    list[i] = { ...list[i], ...updates }
    await acequiaStore.setItem(STORE_KEY, list)
    cache = list
    return list[i]
}

/**
 * The identity — current or kept — that a user id is, with its keys; null
 * when the id isn't this browser's.
 * @returns {Promise<{ userId: string, userKeys: CryptoKeyPair, current: boolean }|null>}
 */
export async function ownIdentityFor(userId) {
    if (!userId) return null
    await acequiaReady()
    if (userId === getUserId()) return { userId, userKeys: getUserKeys(), current: true }
    const kept = (await previousIdentities()).find((p) => p.userId === userId)
    return kept ? { userId, userKeys: kept.userKeys, current: false, kid: kept.kid, linkedTo: kept.linkedTo, aliasAttested: kept.aliasAttested || null } : null
}

/** Whether a user id is this browser's, now or before a link. */
export async function isOwnUserId(userId) {
    return !!(await ownIdentityFor(userId))
}

/** Every user id this browser is or was. */
export async function ownUserIds() {
    await acequiaReady()
    const ids = [getUserId(), ...(await previousIdentities()).map((p) => p.userId)]
    return [...new Set(ids.filter(Boolean))]
}

// ---------------------------------------------------------------------------
// Attesting a kept identity as the account's alias (the server side:
// localWebDAV src/identityAliases.mjs)
// ---------------------------------------------------------------------------

/**
 * Ask the server to hold each kept identity linked into the current account
 * as that account's alias: a statement signed by the kept key ("I am
 * <account>'s"), submitted as the account. Afterwards the server verifies
 * grants rooted in the kept identity as the account's, and the sync queue
 * uploads them. Best-effort; a kept identity the server refuses is marked so.
 * @param {object} [opts]
 * @param {string} [opts.authUrl]
 * @returns {Promise<{ attested: string[], refused: string[], skipped?: string }>}
 */
export async function attestAliases({ authUrl } = {}) {
    await acequiaReady()
    const account = getUserId()
    const accountKeys = getUserKeys()
    const url = authUrl || (typeof location !== 'undefined' ? `${location.origin}/auth` : '/auth')
    const out = { attested: [], refused: [] }
    const pending = (await previousIdentities()).filter((p) => p.linkedTo === account && !p.aliasAttested && !p.aliasRefused)
    if (!pending.length) return out
    if (!account || !accountKeys) return { ...out, skipped: 'no account identity' }
    const { createUserToken } = await import('./tokens.js')
    let bearer
    try {
        bearer = await createUserToken({ purpose: 'identity-alias' }, 300)
    } catch (err) {
        return { ...out, skipped: `no token: ${err.message}` }
    }
    for (const p of pending) {
        try {
            const jwk = await jose.exportJWK(p.userKeys.publicKey)
            const statement = await new jose.SignJWT({ sub: p.userId, aliasOf: account })
                .setProtectedHeader({ alg: 'PS256', typ: 'identity-alias', kid: p.kid, jwk })
                .setIssuedAt()
                .sign(p.userKeys.privateKey)
            const resp = await fetch(`${url}/users/${encodeURIComponent(account)}/aliases`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/jwt', Authorization: `Bearer ${bearer}` },
                body: statement,
            })
            if (resp.ok) {
                await updatePreviousIdentity(p.userId, { aliasAttested: new Date().toISOString() })
                out.attested.push(p.userId)
            } else if (resp.status === 409 || resp.status === 400) {
                const body = await resp.json().catch(() => ({}))
                await updatePreviousIdentity(p.userId, { aliasRefused: body.code || `HTTP ${resp.status}` })
                out.refused.push(p.userId)
            } else if (resp.status === 401) {
                return { ...out, skipped: 'no-session' }
            }
        } catch (err) {
            return { ...out, skipped: `offline: ${err.message}` }
        }
    }
    return out
}
